// ============================================================================
// WeightSparkline — the mini weight trend on the fancy dashboard (Wave 4).
// ----------------------------------------------------------------------------
// A compact, glanceable sparkline of the client's recent weight (home.trend),
// with the latest value large + a signed delta vs. the first point in view. It's
// a SMALL companion to the full TrendChart on /me/report — axis-free, gradient
// area + animated line draw, reduced-motion safe (no path-draw tween). When
// there's <1 reading it shows a gentle "log to start" empty state.
//
// All copy via i18n keys (clientPortal.trend.*); values are the client's own.
// ============================================================================
import { useId, useMemo } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button, Card, CardHeader } from "../../components";
import type { SeriesPoint } from "../../api";
import { CLIENT_LOG_PATH } from "../routes";
import { fmtNum, fmtSigned } from "./portalLib";

const W = 300;
const H = 72;
const PAD = 6;

export function WeightSparkline({ series }: { series: SeriesPoint[] }) {
  const { t } = useTranslation("clientPortal");
  const reduce = useReducedMotion();
  const gradId = useId();

  const model = useMemo(() => {
    if (series.length < 1) return null;
    const ys = series.map((p) => p.y);
    const lo = Math.min(...ys);
    const hi = Math.max(...ys);
    const span = hi - lo || Math.max(Math.abs(hi) * 0.1, 1);
    const innerW = W - PAD * 2;
    const innerH = H - PAD * 2;
    const x = (i: number) =>
      series.length === 1 ? W / 2 : PAD + (innerW * i) / (series.length - 1);
    const y = (v: number) => PAD + innerH * (1 - (v - lo) / span);
    const line = series
      .map((p, i) => `${i === 0 ? "M" : "L"} ${x(i).toFixed(1)} ${y(p.y).toFixed(1)}`)
      .join(" ");
    const area =
      series.length >= 2
        ? `${line} L ${x(series.length - 1).toFixed(1)} ${H - PAD} L ${x(0).toFixed(1)} ${H - PAD} Z`
        : "";
    return {
      line,
      area,
      lastX: x(series.length - 1),
      lastY: y(ys[ys.length - 1]),
      latest: ys[ys.length - 1],
      delta: ys[ys.length - 1] - ys[0],
    };
  }, [series]);

  return (
    <Card className="space-y-3 overflow-hidden">
      <CardHeader title={t("trend.title")} subtitle={t("trend.subtitle")} />

      {!model ? (
        <div className="rounded-lg bg-surface-sunken px-4 py-6 text-center">
          <p className="font-display font-bold text-body">{t("trend.empty")}</p>
          <Link to={`${CLIENT_LOG_PATH}?tab=measurement`}>
            <Button size="sm" className="mt-3">
              {t("trend.cta")}
            </Button>
          </Link>
        </div>
      ) : (
        <div className="flex items-center gap-4">
          <div className="shrink-0">
            <p className="font-display text-3xl font-extrabold tracking-tightest tabular-nums text-body">
              {fmtNum(model.latest, 1)}
              <span className="ml-1 text-base font-bold text-body-soft">{t("trend.unit")}</span>
            </p>
            {series.length >= 2 && (
              <p
                className={`mt-0.5 text-sm font-bold tabular-nums ${
                  model.delta < 0
                    ? "text-lime-600"
                    : model.delta > 0
                      ? "text-volt-600"
                      : "text-body-muted"
                }`}
              >
                {fmtSigned(model.delta, 1)} {t("trend.unit")}
              </p>
            )}
          </div>

          <svg
            viewBox={`0 0 ${W} ${H}`}
            className="h-[72px] min-w-0 flex-1"
            role="img"
            aria-label={t("trend.aria", {
              value: fmtNum(model.latest, 1),
              unit: t("trend.unit"),
            })}
            preserveAspectRatio="none"
          >
            <defs>
              <linearGradient id={`${gradId}-a`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#FF5E62" stopOpacity="0.28" />
                <stop offset="100%" stopColor="#FF9966" stopOpacity="0" />
              </linearGradient>
              <linearGradient id={`${gradId}-l`} x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#FF5E62" />
                <stop offset="100%" stopColor="#FF9966" />
              </linearGradient>
            </defs>
            {model.area && (
              <motion.path
                d={model.area}
                fill={`url(#${gradId}-a)`}
                initial={reduce ? false : { opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: reduce ? 0 : 0.2 }}
              />
            )}
            {series.length >= 2 && (
              <motion.path
                d={model.line}
                fill="none"
                stroke={`url(#${gradId}-l)`}
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                initial={reduce ? false : { pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.9, ease: [0.22, 0.61, 0.36, 1] }}
              />
            )}
            <motion.circle
              cx={model.lastX}
              cy={model.lastY}
              r="4"
              fill="#fff"
              stroke="#FF5E62"
              strokeWidth="3"
              initial={reduce ? false : { scale: 0 }}
              animate={{ scale: 1 }}
              transition={{
                delay: reduce ? 0 : series.length >= 2 ? 0.9 : 0.1,
                type: "spring",
                stiffness: 400,
                damping: 18,
              }}
              style={{ transformOrigin: "center", transformBox: "fill-box" }}
            />
          </svg>
        </div>
      )}
    </Card>
  );
}
